import type { ColumnDef, SortingState, VisibilityState } from '@tanstack/react-table';
import type { UnitRow, UnitsView } from '../../types/safetySystem';
import { fmtMoney } from './components/unitForm';

/**
 * Units grid column sets — port of the V1 Driver / Truck layouts
 * (defs_safety.pl 1373-1503 field lists, mirrored by units_query.py) plus the Payroll /
 * Certificates / Retention / Load Request views (Phase 3 Task 3.7, units_query.py branches).
 */

/** "YYYY-MM-DD[ HH:MM:SS]" (or ISO) -> MM/DD/YYYY; zero/blank dates render empty. */
export const shortDate = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (!s || s.startsWith('0000') || s === '0') return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

const shortDateTime = (value: unknown): string => {
  const date = shortDate(value);
  if (!date) return '';
  const m = String(value).match(/[T ](\d{2}):(\d{2})/);
  return m ? `${date} ${m[1]}:${m[2]}` : date;
};

const dateCol = (accessorKey: string, header: string, size = 110): ColumnDef<UnitRow> => ({
  accessorKey,
  header,
  size,
  cell: ({ getValue }) => shortDate(getValue()),
});

const dateTimeCol = (accessorKey: string, header: string, size = 140): ColumnDef<UnitRow> => ({
  accessorKey,
  header,
  size,
  cell: ({ getValue }) => shortDateTime(getValue()),
});

const yn = (accessorKey: string, header: string, size = 80): ColumnDef<UnitRow> => ({
  accessorKey,
  header,
  size,
  cell: ({ getValue }) => (getValue() === 'Y' ? 'Yes' : getValue() === 'N' ? 'No' : ''),
});

/** Status chip: in service = site accent, out of service = danger (with the OOS type), terminated = dark. */
export function StatusChip({ status, oosType }: { status: string | null | undefined; oosType?: string | null }) {
  const s = (status || '').toUpperCase();
  if (!s) return <span className="text-muted">—</span>;
  let cls = 'bg-gray-200 text-heading dark:bg-gray-700';
  let text = s;
  if (s === 'IN SERVICE') {
    cls = 'bg-accent text-accent-fg';
  } else if (s === 'OUT OF SERVICE') {
    cls = 'bg-danger text-danger-fg';
    text = oosType ? `OOS · ${oosType}` : 'OUT OF SERVICE';
  } else if (s === 'TERMINATED') {
    cls = 'bg-gray-900 text-white dark:bg-black';
  }
  return (
    <span
      title={s}
      className={`inline-block max-w-full truncate rounded px-1.5 py-0.5 text-xs font-semibold uppercase leading-4 ${cls}`}
    >
      {text}
    </span>
  );
}

const COMMON_LEAD: ColumnDef<UnitRow>[] = [
  { accessorKey: 'unitnumber', header: 'Unit', size: 80 },
  { accessorKey: 'drivername', header: 'Driver', size: 180 },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 150,
    cell: ({ row }) => <StatusChip status={row.original.status} oosType={row.original.outofservicetype} />,
  },
  { accessorKey: 'oosdays', header: 'OOS Days', size: 90, meta: { align: 'right' } },
  { accessorKey: 'outofservicetype', header: 'OOS Type', size: 130 },
  dateCol('outofservicedate', 'OOS Date'),
  { accessorKey: 'outofservicecomments', header: 'OOS Comments', size: 220 },
  { accessorKey: 'region', header: 'Region', size: 120 },
];

const COMMON_TAIL: ColumnDef<UnitRow>[] = [
  { accessorKey: 'preference', header: 'Preference', size: 100 },
  { accessorKey: 'companyrep', header: 'Company Rep', size: 130 },
  { accessorKey: 'dispatcher', header: 'Dispatcher', size: 130 },
  { accessorKey: 'drivertype', header: 'Driver Type', size: 130 },
  yn('muledriver', 'Mule'),
  { accessorKey: 'parkingloc', header: 'Parking Loc', size: 140 },
  dateCol('estimatedreturndate', 'Est. Return'),
  dateCol('contractdate', 'Contract Date'),
  dateCol('startdate', 'Start Date'),
  dateCol('terminatedate', 'Terminate Date'),
];

export const DRIVER_COLUMNS: ColumnDef<UnitRow>[] = [
  ...COMMON_LEAD,
  ...COMMON_TAIL,
  { accessorKey: 'add1', header: 'Address', size: 180 },
  { accessorKey: 'city', header: 'City', size: 120 },
  { accessorKey: 'state', header: 'State', size: 70 },
  { accessorKey: 'zip', header: 'Zip', size: 80 },
  { accessorKey: 'homephone', header: 'Home Phone', size: 120 },
  { accessorKey: 'textmsgnumber', header: 'Cell / Text', size: 120 },
  { accessorKey: 'email', header: 'Email', size: 200 },
  dateCol('dateofbirth', 'Date of Birth'),
  { accessorKey: 'ssn', header: 'SSN (last 4)', size: 100 },
  { accessorKey: 'language', header: 'Language', size: 100 },
  { accessorKey: 'experience', header: 'Experience', size: 100 },
  yn('usegwimove', 'GWI Move'),
  { accessorKey: 'mobilepin', header: 'Mobile PIN', size: 100 },
  { accessorKey: 'deviceserialnum', header: 'Device Serial', size: 140 },
  { accessorKey: 'devicephonenum', header: 'Device Phone', size: 120 },
  yn('daycab', 'Day Cab'),
  { accessorKey: 'truckvin', header: 'Truck VIN', size: 170 },
  { accessorKey: 'lastdrivernote', header: 'Last Note', size: 240 },
  { accessorKey: 'lastdrivernoteby', header: 'Note By', size: 110 },
  dateTimeCol('lastdrivernotedatetime', 'Note Date'),
  // Excel-extras (hidden by default, see DRIVER_DEFAULT_HIDDEN)
  dateCol('driverstartdate', 'Driver Start'),
  { accessorKey: 'trainer', header: 'Trainer', size: 130 },
  { accessorKey: 'driverlicnum', header: 'License #', size: 130 },
  { accessorKey: 'fueltype', header: 'Fuel Type', size: 100 },
  { accessorKey: 'fuelcardnumber', header: 'Fuel Card #', size: 130 },
];

export const DRIVER_DEFAULT_HIDDEN: VisibilityState = {
  mobilepin: false, // V1 parity keeps it in the dataset; it is a credential, so hidden until opted in
  driverstartdate: false,
  trainer: false,
  driverlicnum: false,
  fueltype: false,
  fuelcardnumber: false,
};

export const TRUCK_COLUMNS: ColumnDef<UnitRow>[] = [
  ...COMMON_LEAD,
  { accessorKey: 'truckvin', header: 'VIN', size: 170 },
  { accessorKey: 'truckyear', header: 'Year', size: 70 },
  { accessorKey: 'truckmake', header: 'Make', size: 110 },
  { accessorKey: 'truckmodel', header: 'Model', size: 110 },
  { accessorKey: 'truckcolor', header: 'Color', size: 90 },
  yn('cmv', 'CMV'),
  { accessorKey: 'truckweight', header: 'Weight', size: 90, meta: { align: 'right' } },
  { accessorKey: 'trucklicplate', header: 'Plate', size: 100 },
  { accessorKey: 'trucklictype', header: 'Plate Type', size: 100 },
  yn('daycab', 'Day Cab'),
  { accessorKey: 'truckowner_display', header: 'Truck Owner', size: 160 },
  { accessorKey: 'truckownername', header: 'Owner Name', size: 160 },
  { accessorKey: 'truckownerdba', header: 'Owner DBA', size: 160 },
  { accessorKey: 'truckowneradd1', header: 'Owner Address', size: 180 },
  { accessorKey: 'truckownercity', header: 'Owner City', size: 120 },
  { accessorKey: 'truckownerstate', header: 'Owner State', size: 90 },
  { accessorKey: 'truckownerzip', header: 'Owner Zip', size: 90 },
  { accessorKey: 'truckownercell', header: 'Owner Cell', size: 120 },
  { accessorKey: 'truckowneremail', header: 'Owner Email', size: 200 },
  { accessorKey: 'lienholder', header: 'Lienholder', size: 140 },
  yn('titleloan', 'Title Loan'),
  dateCol('dotinspecexpdate', 'DOT Insp. Exp'),
  dateCol('registrationexpdate', 'Registration Exp'),
  dateCol('companyinspecexpdate', 'Company Insp. Exp'),
  dateCol('iftaexpdate', 'IFTA Exp'),
  { accessorKey: 'iftacertnum', header: 'IFTA Cert #', size: 120 },
  ...COMMON_TAIL,
  { accessorKey: 'lasttrucknote', header: 'Last Note', size: 240 },
  { accessorKey: 'lasttrucknoteby', header: 'Note By', size: 110 },
  dateTimeCol('lasttrucknotedatetime', 'Note Date'),
  yn('truckdeleted', 'Deleted'),
];

export const TRUCK_DEFAULT_HIDDEN: VisibilityState = {
  truckdeleted: false,
  companyrep: false,
  dispatcher: false,
  drivertype: false,
};

export const UNITS_DATE_COLUMNS = [
  'outofservicedate', 'estimatedreturndate', 'contractdate', 'startdate', 'terminatedate', 'dateofbirth',
  'driverstartdate', 'lastdrivernotedatetime', 'dotinspecexpdate', 'registrationexpdate', 'companyinspecexpdate',
  'iftaexpdate', 'lasttrucknotedatetime',
  // Payroll / cert / retention / loadrequest views
  'enddate', 'licdate', 'licexpdate', 'lastsurveydate', 'lastworked',
];

// ---------------------------------------------------------------------------
// Payroll / Certificates / Retention / Load Request views
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;
const EXPIRY_WARN_DAYS = 30;
const IDLE_WARN_DAYS = 7;

/** Local-midnight Date for "YYYY-MM-DD…" (null for blank / zero dates / unparsable). */
const dateOnly = (value: unknown): Date | null => {
  if (value === null || value === undefined) return null;
  const s = String(value);
  if (!s || s.startsWith('0000') || s === '0') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Cert `licexpdate` tint: danger once past, warning within 30 days (inclusive of today). */
export const expiryTintClass = (value: unknown, today: Date = new Date()): string => {
  const d = dateOnly(value);
  if (!d) return '';
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((d.getTime() - start.getTime()) / DAY_MS);
  if (days < 0) return 'text-danger font-semibold';
  if (days <= EXPIRY_WARN_DAYS) return 'text-warning font-semibold';
  return '';
};

/** Load Request `numdays` (days since last dispatch) tint once the driver has sat idle more than a week. */
export const numDaysTintClass = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) && n > IDLE_WARN_DAYS ? 'text-warning font-semibold' : '';
};

const moneyCol = (accessorKey: string, header: string, size = 110): ColumnDef<UnitRow> => ({
  accessorKey,
  header,
  size,
  meta: { align: 'right' },
  cell: ({ getValue }) => { const v = getValue(); return v === null || v === undefined || v === '' ? '' : fmtMoney(v); },
});

/** Payroll `source`: which child table the row came from (V1 deductions UNION insurance). */
export function SourceChip({ source }: { source: unknown }) {
  const s = String(source || '').toUpperCase();
  if (!s) return null;
  const cls = s === 'INSURANCE' ? 'bg-info text-info-fg' : 'bg-gray-200 text-heading dark:bg-gray-700';
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold uppercase leading-4 ${cls}`}>{s}</span>;
}

/** The three identity columns every unit-only view starts with (units_query._IDENT). */
const VIEW_LEAD: ColumnDef<UnitRow>[] = [
  { accessorKey: 'unitnumber', header: 'Unit', size: 80 },
  { accessorKey: 'drivername', header: 'Driver', size: 180 },
  { accessorKey: 'status', header: 'Status', size: 150, cell: ({ row }) => <StatusChip status={row.original.status} /> },
];

export const PAYROLL_COLUMNS: ColumnDef<UnitRow>[] = [
  ...VIEW_LEAD,
  { accessorKey: 'source', header: 'Source', size: 110, cell: ({ getValue }) => <SourceChip source={getValue()} /> },
  { accessorKey: 'type', header: 'Type', size: 160 },
  moneyCol('amount', 'Amount'),
  dateCol('startdate', 'Start'),
  dateCol('enddate', 'End'),
  { accessorKey: 'refno', header: 'Ref #', size: 120 },
];

export const CERT_COLUMNS: ColumnDef<UnitRow>[] = [
  ...VIEW_LEAD,
  { accessorKey: 'lictype', header: 'Certificate', size: 200 },
  dateCol('licdate', 'Issued'),
  {
    accessorKey: 'licexpdate',
    header: 'Expires',
    size: 110,
    cell: ({ getValue }) => {
      const v = getValue();
      const tint = expiryTintClass(v);
      return tint ? <span className={tint}>{shortDate(v)}</span> : shortDate(v);
    },
  },
  { accessorKey: 'licnum', header: 'Number', size: 150 },
];

export const RETENTION_COLUMNS: ColumnDef<UnitRow>[] = [
  ...VIEW_LEAD,
  { accessorKey: 'companyrep', header: 'Company Rep', size: 130 },
  { accessorKey: 'dispatcher', header: 'Dispatcher', size: 130 },
  { accessorKey: 'country', header: 'Country', size: 120 },
  { accessorKey: 'tshirtsize', header: 'T-Shirt', size: 80 },
  { accessorKey: 'numsurveys', header: 'Surveys', size: 90, meta: { align: 'right' } },
  { accessorKey: 'numawards', header: 'Awards', size: 90, meta: { align: 'right' } },
  dateCol('lastsurveydate', 'Last Survey'),
  { accessorKey: 'lastsurveyrating', header: 'Last Rating', size: 100, meta: { align: 'right' } },
];

export const LOADREQUEST_COLUMNS: ColumnDef<UnitRow>[] = [
  ...VIEW_LEAD,
  { accessorKey: 'region', header: 'Region', size: 120 },
  { accessorKey: 'preference', header: 'Preference', size: 100 },
  { accessorKey: 'email', header: 'Email', size: 200 },
  { accessorKey: 'textmsgnumber', header: 'Cell / Text', size: 120 },
  { accessorKey: 'language', header: 'Language', size: 100 },
  dateTimeCol('lastworked', 'Last Worked'),
  {
    accessorKey: 'numdays',
    header: 'Days Idle',
    size: 90,
    meta: { align: 'right' },
    cell: ({ getValue }) => {
      const v = getValue();
      const text = v === null || v === undefined ? '' : String(v);
      const tint = numDaysTintClass(v);
      return tint ? <span className={tint}>{text}</span> : text;
    },
  },
  { accessorKey: 'currdispatch', header: 'Current Pro', size: 110 },
  { accessorKey: 'dispatchstatus', header: 'Dispatch Status', size: 130 },
  moneyCol('loans', 'Loans'),
  moneyCol('deferments', 'Deferments'),
  moneyCol('escrow', 'Escrow'),
  moneyCol('difference', 'Difference'),
];

export const VIEW_TITLES: Record<UnitsView, string> = {
  driver: 'Units — Driver view',
  truck: 'Units — Truck view',
  payroll: 'Units — Payroll',
  cert: 'Units — Certificates',
  retention: 'Units — Retention',
  loadrequest: 'Units — Load Request',
};

export const unitsColumnsFor = (view: UnitsView): ColumnDef<UnitRow>[] => {
  switch (view) {
    case 'truck': return TRUCK_COLUMNS;
    case 'payroll': return PAYROLL_COLUMNS;
    case 'cert': return CERT_COLUMNS;
    case 'retention': return RETENTION_COLUMNS;
    case 'loadrequest': return LOADREQUEST_COLUMNS;
    default: return DRIVER_COLUMNS;
  }
};

export const unitsDefaultHiddenFor = (view: UnitsView): VisibilityState => {
  switch (view) {
    case 'truck': return TRUCK_DEFAULT_HIDDEN;
    case 'driver': return DRIVER_DEFAULT_HIDDEN;
    default: return {};
  }
};

/** Payroll / cert rows are child rows (several per unit) keyed by `rowid`; every other view is one row per unit. */
export const unitsRowId = (view: UnitsView, r: UnitRow): string => {
  if ((view === 'payroll' || view === 'cert') && r.rowid !== null && r.rowid !== undefined) return String(r.rowid);
  return String(r.unitrecno ?? `orphan-${r.driverrecno}-${r.truckrecno}`);
};

export const unitsDefaultSorting = (view: UnitsView): SortingState => {
  switch (view) {
    case 'cert': return [{ id: 'licexpdate', desc: false }];
    case 'loadrequest': return [{ id: 'numdays', desc: true }];
    default: return [{ id: 'unitnumber', desc: false }];
  }
};
